import React, { forwardRef, useCallback, useImperativeHandle, useState } from 'react';

import { Box, Icon, Image, ScrollView, Text, View } from '@rbilabs/universal-components';
import { usePaymentContext } from 'features/payment';
import { IPaymentMethod } from 'features/payment/types';
import { useIntl } from 'react-intl';

import Currency from 'components/currency';
import { useCardNumber } from 'components/gift-card-modal/use-giftcard-number';
import { CurrentSelectedMethodType } from 'components/payment-method/types';
import { toaster } from 'components/toaster/toaster';
import { KeyboardAwareView } from 'components/ucl/keyboard-aware-view';
import {
  BackgroundAndLogoContainer,
  GiftCardContainer,
  LogoAndNameContainer,
} from 'experiments/checkout-v2/control/pages/cart/payment/order-payment/add-gift-card-redesign/styled';
import { TransferBalanceContainer } from 'experiments/checkout-v2/control/pages/cart/payment/order-payment/transfer-gift-card-redesign/transfer-balance';
import { AddGiftCardInput } from 'experiments/checkout-v2/treatment/pages/cart/payment/add-gift-card-input';
import { usePrepaidByNumber } from 'hooks/prepaid';
import { primitive } from 'styles/constants/primitives';

import { AddEditMethodProps } from './types';

const crownsImg = require('styles/images/crowns-image.png');

export type ReloadMethodRef = {
  reloadCard: () => void;
};
export const ReloadPrepaidMethod = forwardRef<ReloadMethodRef, AddEditMethodProps>(
  ({ openErrorDialog, isLoading, setIsLoading }, ref) => {
    const { formatMessage } = useIntl();
    const {
      setIsAddReloadPrepaidMethodModalVisible,
      getPrepaidPaymentMethod,
      setCheckoutPaymentMethodId,
      setDefaultPaymentMethodId,
      mergePrepaidCardBalances,
      getBalanceFromPaymentMethods,
    } = usePaymentContext();
    const {
      giftCardNumber,
      errors,
      handleCardNumberChange,
      isValidGiftCardNumberCheck,
      validateCardNumber,
    } = useCardNumber();
    const { getPrepaidByNumber } = usePrepaidByNumber();

    const [cardBalance, setCardBalance] = useState(0);
    const cardNumber = giftCardNumber?.replace(/\s+/g, '');
    const showBalance = !isLoading && !errors.giftCardNumber && cardBalance > 0;
    const paymentMethod: IPaymentMethod | null = getPrepaidPaymentMethod();
    const currentAmount = (paymentMethod && getBalanceFromPaymentMethods(paymentMethod)) || 0;
    const [oldGiftCardNumber, setOldGiftCardNumber] = useState('');

    const showGiftCardAddingErrorModal = useCallback(
      (error: unknown) => {
        // @ts-expect-error TS(2322) FIXME: Type 'unknown' is not assignable to type 'Error | ... Remove this comment to see the full error message
        openErrorDialog({ message: formatMessage({ id: 'giftCardAddingError' }), error });
      },
      [formatMessage, openErrorDialog]
    );

    const transferCard = useCallback(async () => {
      if (cardNumber) {
        const { data } = await getPrepaidByNumber({ cardNumber });
        if (!data) {
          showGiftCardAddingErrorModal(errors);
          setCardBalance(0);
        } else {
          if (data?.balance === 0) {
            openErrorDialog({ message: formatMessage({ id: 'giftCardAlreadyRedeem' }) });
          } else {
            setCardBalance(data?.balance || 0);
          }
        }
        setIsLoading(false);
      }
    }, [
      cardNumber,
      errors,
      formatMessage,
      getPrepaidByNumber,
      openErrorDialog,
      setIsLoading,
      showGiftCardAddingErrorModal,
    ]);

    const handleGiftCardChange = useCallback(async () => {
      setIsLoading(true);
      handleCardNumberChange(cardNumber);
      validateCardNumber(cardNumber);

      if (isValidGiftCardNumberCheck(cardNumber)) {
        await transferCard();
      } else if (errors) {
        setIsLoading(false);
      }
    }, [
      cardNumber,
      errors,
      handleCardNumberChange,
      isValidGiftCardNumberCheck,
      setIsLoading,
      transferCard,
      validateCardNumber,
    ]);

    const confirmTransferCard = useCallback(async () => {
      try {
        if (!errors.giftCardNumber && cardBalance > 0) {
          setIsLoading(true);
          await mergePrepaidCardBalances({
            sourceCardNumber: giftCardNumber || '',
          });
          // Setting Default Payment method
          setCheckoutPaymentMethodId(CurrentSelectedMethodType.ADD_NEW_GIFT_CARD);
          setDefaultPaymentMethodId(CurrentSelectedMethodType.ADD_NEW_GIFT_CARD);

          toaster.positive(formatMessage({ id: 'transferBalanceCompletedMessage' }));
          setIsAddReloadPrepaidMethodModalVisible(false);
        }
      } catch (error) {
        // @ts-expect-error TS(2322) FIXME: Type 'unknown' is not assignable to type 'Error | ... Remove this comment to see the full error message
        openErrorDialog({ message: formatMessage({ id: 'giftCardAddingError' }), error });
      } finally {
        setIsLoading(false);
      }
    }, [
      cardBalance,
      errors.giftCardNumber,
      formatMessage,
      giftCardNumber,
      mergePrepaidCardBalances,
      openErrorDialog,
      setCheckoutPaymentMethodId,
      setDefaultPaymentMethodId,
      setIsAddReloadPrepaidMethodModalVisible,
      setIsLoading,
    ]);

    useImperativeHandle(
      ref,
      () => ({
        reloadCard: async () => {
          if (showBalance) {
            await confirmTransferCard();
          } else {
            setOldGiftCardNumber(giftCardNumber);
            await handleGiftCardChange();
          }
        },
      }),
      [confirmTransferCard, giftCardNumber, handleGiftCardChange, showBalance]
    );

    return (
      <>
        <ScrollView>
          <GiftCardContainer>
            <BackgroundAndLogoContainer>
              <LogoAndNameContainer>
                <Icon accessibilityHidden variant="giftCard" size="xl" mb="$1" />
                <Text
                  accessible={true}
                  accessibilityRole="header"
                  accessibilityLabel={formatMessage({ id: 'transferGiftCard' })}
                  color={primitive.bk.$bbqBrown}
                  fontFamily="heading"
                  lineHeight="24px"
                  fontSize="2xl"
                  mb="$2"
                >
                  {formatMessage({ id: 'giftCard' })}
                </Text>
                <Box width="full" flexDirection="row" mb="$1">
                  <Text fontSize="sm">{formatMessage({ id: 'currentBalance' })}</Text>

                  <Currency
                    amount={currentAmount}
                    textProps={{
                      margin: 0,
                      fontSize: 'sm',
                    }}
                  />
                </Box>
                {showBalance && (
                  <Box width="full" flexDirection="row" mt="$1">
                    <Text fontSize="sm" fontWeight="bold">
                      {formatMessage({ id: 'newBalance' })}
                    </Text>

                    <Currency
                      amount={currentAmount + cardBalance}
                      textProps={{
                        margin: 0,
                        fontSize: 'sm',
                      }}
                      isBold
                    />
                  </Box>
                )}
              </LogoAndNameContainer>
              <Image source={crownsImg} width={162} height={145} marginTop="$7" />
            </BackgroundAndLogoContainer>
          </GiftCardContainer>

          {!showBalance && (
            <KeyboardAwareView withScroll>
              <View style={{ paddingHorizontal: primitive.spacing.$4 }}>
                <AddGiftCardInput
                  onChange={handleCardNumberChange}
                  errorMessage={errors?.giftCardNumber}
                  label={formatMessage({ id: 'cardNumber' })}
                  testID="giftcard-input"
                  initialValue={oldGiftCardNumber}
                  isRedesign={true}
                  isLoading={isLoading}
                />
              </View>
            </KeyboardAwareView>
          )}
          {showBalance && (
            <TransferBalanceContainer cardBalance={cardBalance} giftCardNumber={giftCardNumber} />
          )}
        </ScrollView>
      </>
    );
  }
);
